import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import {
  RocketLaunch,
  TrendingUp,
  SwapHoriz,
  ShieldOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const MarketRegimeCard = ({ regimeData, interpretation }) => {
  const { t } = useTranslation();

  const regimeConfig = {
    'RISK_ON': { color: '#10b981', bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', icon: RocketLaunch, label: 'RISK ON' },
    'RECOVERY': { color: '#3b82f6', bg: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', icon: TrendingUp, label: 'RECOVERY' },
    'SIDEWAY': { color: '#f59e0b', bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', icon: SwapHoriz, label: 'SIDEWAY' },
    'RISK_OFF': { color: '#ef4444', bg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', icon: ShieldOutlined, label: 'RISK OFF' },
  };

  const cfg = regimeConfig[regimeData?.regime] || regimeConfig['SIDEWAY'];
  const score = regimeData?.market_score || 0;
  const hasDerivatives = regimeData?.deriv_score != null;
  const derivSignals = regimeData?.derivatives_signals;

  const getBasisColor = (level) => ({
    'strong_premium': '#059669', 'moderate_premium': '#10b981', 'neutral': '#64748b',
    'moderate_discount': '#f97316', 'strong_discount': '#ef4444'
  }[level] || '#64748b');

  const getBasisLabel = (level) => t({
    'strong_premium': 'safeInvest.layer1.basis_strong_premium',
    'moderate_premium': 'safeInvest.layer1.basis_moderate_premium',
    'neutral': 'safeInvest.layer1.basis_neutral',
    'moderate_discount': 'safeInvest.layer1.basis_moderate_discount',
    'strong_discount': 'safeInvest.layer1.basis_strong_discount'
  }[level] || 'safeInvest.layer1.basis_neutral');

  // Horizontal bar data - single color scheme
  const bars = [
    { label: t('safeInvest.layer1.trend'), value: regimeData?.trend_score || 0 },
    { label: t('safeInvest.layer1.breadth'), value: regimeData?.breadth_score || 0 },
    { label: t('safeInvest.layer1.liquidity'), value: regimeData?.liquidity_score || 0 },
    { label: t('safeInvest.layer1.risk'), value: regimeData?.risk_score || 0 },
    ...(hasDerivatives ? [{ label: t('safeInvest.layer1.derivatives'), value: regimeData?.deriv_score || 0 }] : []),
  ];

  // Get bar color based on value
  const getBarColor = (value) => {
    if (value >= 70) return '#10b981'; // Green
    if (value >= 50) return '#3b82f6'; // Blue
    if (value >= 30) return '#f59e0b'; // Orange
    return '#ef4444'; // Red
  };

  return (
    <Paper elevation={0} sx={{ 
      mb: 2, 
      overflow: 'hidden', 
      border: '1px solid #e5e7eb', 
      borderRadius: 2.5 
    }}>
      {/* Header - Compact */}
      <Box sx={{ 
        background: cfg.bg,
        color: 'white',
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {React.createElement(cfg.icon, { sx: { fontSize: 24, color: 'white' } })}
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.55rem', opacity: 0.85, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600, mb: 0.2 }}>
              {t('safeInvest.layer1.currentRegime')}
            </Typography>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, letterSpacing: -0.5, lineHeight: 1 }}>
              {cfg.label}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontSize: '2.2rem', fontWeight: 900, lineHeight: 1 }}>
            {score.toFixed(0)}
          </Typography>
          <Typography sx={{ fontSize: '0.55rem', opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('safeInvest.layer1.marketScore')}
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'right', maxWidth: 240 }}>
          <Typography sx={{ fontSize: '0.7rem', opacity: 0.9, fontWeight: 600, mb: 0.3 }}>
            {interpretation?.stance ? t(`safeInvest.layer1.stances.${interpretation.stance}`, interpretation.stance) : '-'} • {interpretation?.exposure || '-'}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.3 }}>
            {regimeData?.regime === 'RISK_ON' && t('safeInvest.layer1.action_risk_on')}
            {regimeData?.regime === 'RECOVERY' && t('safeInvest.layer1.action_recovery')}
            {regimeData?.regime === 'SIDEWAY' && t('safeInvest.layer1.action_sideway')}
            {regimeData?.regime === 'RISK_OFF' && t('safeInvest.layer1.action_risk_off')}
          </Typography>
        </Box>
      </Box>

      {/* Body - Ultra Compact */}
      <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
        {/* Component Score Bars - 2 Columns, Tight */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px 16px' }}>
          {bars.map((bar, i) => {
            const barColor = getBarColor(bar.value);
            return (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* Label */}
                <Typography sx={{ 
                  width: 85, 
                  fontSize: '0.7rem', 
                  color: '#475569', 
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.3
                }}>
                  {bar.label}
                </Typography>
                
                {/* Bar container - Full width */}
                <Box sx={{ flex: 1, position: 'relative', height: 12 }}>
                  {/* Background */}
                  <Box sx={{ 
                    position: 'absolute', 
                    inset: 0, 
                    bgcolor: '#e5e7eb',
                    borderRadius: 1.5,
                  }} />
                  
                  {/* Value bar */}
                  <Box sx={{ 
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: `${bar.value}%`,
                    bgcolor: barColor,
                    borderRadius: 1.5,
                    transition: 'width 0.4s ease',
                  }} />
                </Box>
                
                {/* Value */}
                <Typography sx={{ 
                  width: 42,
                  fontSize: '0.85rem',
                  fontWeight: 800,
                  color: barColor,
                  fontFamily: 'monospace',
                  textAlign: 'right'
                }}>
                  {bar.value.toFixed(1)}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Risk Metrics & Derivatives - Ultra Compact Single Line */}
        <Box sx={{ 
          mt: 1.5,
          pt: 1.5,
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          flexWrap: 'wrap'
        }}>
          {/* ATR */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>
              ATR:
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', fontFamily: 'monospace' }}>
              {(regimeData?.atr_pct * 100)?.toFixed(2)}%
            </Typography>
          </Box>

          {/* Drawdown */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>
              DD20:
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#ef4444', fontFamily: 'monospace' }}>
              -{(regimeData?.dd20 * 100)?.toFixed(2)}%
            </Typography>
          </Box>

          {/* VN30F1M Basis */}
          {hasDerivatives && derivSignals && (
            <>
              <Typography sx={{ color: '#d1d5db', mx: -0.5 }}>|</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>
                  VN30F1M:
                </Typography>
                <Typography sx={{ 
                  fontSize: '0.85rem', 
                  fontWeight: 700, 
                  color: getBasisColor(derivSignals.basis_interpretation?.level),
                  fontFamily: 'monospace'
                }}>
                  {derivSignals.basis_pct > 0 ? '+' : ''}{(derivSignals.basis_pct * 100).toFixed(2)}%
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 500 }}>
                  {getBasisLabel(derivSignals.basis_interpretation?.level)}
                </Typography>
              </Box>
            </>
          )}

          {/* Hedge */}
          {regimeData?.hedge_overlay && (
            <>
              <Typography sx={{ color: '#d1d5db', mx: -0.5 }}>|</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 600 }}>
                  HEDGE:
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#8b5cf6', fontFamily: 'monospace' }}>
                  {(regimeData.hedge_overlay.hedge_ratio * 100).toFixed(0)}%
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 500 }}>
                  ({regimeData.hedge_overlay.hedge_contracts})
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default MarketRegimeCard;